import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Card, Form, Button, Row, Col, Alert, Spinner, Image, Badge } from 'react-bootstrap';
import { useAuthContext } from '../context/AuthContext';
import { useAlertModal } from '../AlertModalContext';
import InputMask from 'react-input-mask';
import SignatureCanvas from 'react-signature-canvas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileSignature, faEraser, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const PerfilUsuarioPage = () => {
    // Hooks de contexto para obter dados do usuário e mostrar alertas
    const { loggedInUser, updateLoggedInUser } = useAuthContext();
    const { showAlertModal } = useAlertModal();
    const fileInputRef = useRef(null);
    const sigPadRef = useRef(null);

    const [userData, setUserData] = useState({
        nome: '',
        cpf: '',
        email: '',
        profile_name: '',
        dddcelular: '',
        celular: '',
        // Novos campos
        data_nascimento: '',
        estado_civil: '',
        mae: '',
        pai: '',
        telefoneCompleto: '',
        assinatura_path: '', // Para controle do preview da assinatura atual
    });
    const [passwordData, setPasswordData] = useState({
        new_password: '',
        new_password_confirmation: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isSavingSignature, setIsSavingSignature] = useState(false);
    const [message, setMessage] = useState(null);
    const [signatureMessage, setSignatureMessage] = useState(null);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    // Popula o formulário com os dados do usuário logado
    useEffect(() => {
        if (loggedInUser) {
            const telefone = `${loggedInUser.dddcelular || ''}${loggedInUser.celular || ''}`;
            setUserData({
                nome: loggedInUser.nome || '',
                cpf: loggedInUser.cpf || '',
                email: loggedInUser.email || '',
                profile_name: loggedInUser.profile_name || 'N/A',
                dddcelular: loggedInUser.dddcelular || '',
                celular: loggedInUser.celular || '',
                // Popula os novos campos
                data_nascimento: loggedInUser.data_nascimento ? loggedInUser.data_nascimento.split(' ')[0] : '', // Pega apenas a data
                estado_civil: loggedInUser.estado_civil || 'Nao informado',
                mae: loggedInUser.mae || '',
                pai: loggedInUser.pai || '',
                assinatura_path: loggedInUser.assinatura_path || '',
                telefoneCompleto: telefone,
            });
        }
    }, [loggedInUser]);

    // Handler para campos de dados do usuário
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'telefoneCompleto') {
            const apenasNumeros = value.replace(/\D/g, '');
            const ddd = apenasNumeros.substring(0, 2);
            const celular = apenasNumeros.substring(2);
            setUserData(prev => ({ ...prev, telefoneCompleto: value, dddcelular: ddd, celular: celular }));
        } else {
            setUserData(prev => ({ ...prev, [name]: value }));
        }
    };

    // Handler para campos de senha
    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
    };

    // Handler para a seleção de foto
    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) {
            return;
        }

        // Define o tamanho alvo para o redimensionamento
        const targetSize = 250;
        const reader = new FileReader();

        reader.onload = (event) => {
            const img = document.createElement('img');
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = targetSize;
                canvas.height = targetSize;

                const imgAspectRatio = img.width / img.height;
                const canvasAspectRatio = 1; // targetSize / targetSize is 1

                let sx = 0, sy = 0, sWidth = img.width, sHeight = img.height;

                // Calcula as dimensões para cortar a imagem mantendo o aspect ratio (efeito "cover")
                if (imgAspectRatio > canvasAspectRatio) {
                    sWidth = img.height * canvasAspectRatio;
                    sx = (img.width - sWidth) / 2;
                } else {
                    sHeight = img.width / canvasAspectRatio;
                    sy = (img.height - sHeight) / 2;
                }

                // Desenha a imagem cortada e redimensionada no canvas
                ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, targetSize, targetSize);

                // Converte o canvas para um Blob e atualiza o estado
                canvas.toBlob((blob) => {
                    const resizedFile = new File([blob], file.name, { type: file.type, lastModified: Date.now() });
                    setPhotoFile(resizedFile);
                    if (photoPreview) URL.revokeObjectURL(photoPreview);
                    setPhotoPreview(URL.createObjectURL(resizedFile));
                }, file.type, 0.9); // 0.9 é a qualidade para JPEG/WebP
            };
            img.src = event.target.result;
        };

        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage(null);

        if (passwordData.new_password && passwordData.new_password !== passwordData.new_password_confirmation) {
            setMessage({ type: 'danger', text: 'A nova senha e a confirmação não correspondem.' });
            return;
        }

        setIsLoading(true);

        // Usa FormData para enviar arquivos e dados
        const formData = new FormData();
        
        // Adiciona os dados do usuário
        formData.append('dddcelular', userData.dddcelular);
        formData.append('celular', userData.celular);
        formData.append('data_nascimento', userData.data_nascimento || '');
        formData.append('estado_civil', userData.estado_civil);
        formData.append('mae', userData.mae);
        formData.append('pai', userData.pai);

        // Adiciona a senha ao payload apenas se foi preenchida
        if (passwordData.new_password) {
            formData.append('new_password', passwordData.new_password);
            formData.append('new_password_confirmation', passwordData.new_password_confirmation);
        }

        // Adiciona a foto se uma nova foi selecionada
        if (photoFile) {
            formData.append('foto', photoFile);
        }

        try {
            // A requisição POST para /api/perfil já está correta.
            const response = await axios.post('/api/perfil', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // 1. Verifica se a requisição foi bem-sucedida (status 200) e se a resposta contém os dados esperados.
            if (response.status === 200 && response.data && response.data.funcionario) {
                // 2. Atualiza o contexto do usuário com os novos dados retornados pela API.
                // A API retorna o modelo 'Funcionario', que é a estrutura que o contexto espera.
                
                // 3. Exibe a mensagem de sucesso usando o modal de alerta global.
                showAlertModal('Sucesso', response.data.message || 'Perfil atualizado com sucesso!', 'success');
                
                // // 4. Limpa os estados locais do formulário após o sucesso.
                // setPhotoFile(null);
                // setPhotoPreview(null);
                setPasswordData({ new_password: '', new_password_confirmation: '' });
            } else {
                // Caso a resposta não seja a esperada, mesmo com status 200.
                throw new Error('A resposta do servidor não foi a esperada.');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Ocorreu um erro ao atualizar o perfil.';
            // Usa o modal de alerta global para exibir o erro, em vez do estado local 'message'.
            showAlertModal('Erro de Atualização', errorMessage, 'danger');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClearSignature = () => {
        sigPadRef.current.clear();
    };

    const handleSaveSignature = async () => {
        if (sigPadRef.current.isEmpty()) {
            setSignatureMessage({ type: 'warning', text: 'Por favor, desenhe sua assinatura antes de salvar.' });
            return;
        }

        setIsSavingSignature(true);
        const signatureBase64 = sigPadRef.current.toDataURL('image/png');

        try {
            const response = await axios.post(`/api/funcionarios/${loggedInUser.id}/update-signature`, {
                assinatura: signatureBase64
            });
            setSignatureMessage({ type: 'success', text: 'Sua assinatura profissional foi salva com sucesso!' });
            
            // Atualiza o estado local para mostrar o novo preview da assinatura salva
            setUserData(prev => ({ ...prev, assinatura_path: response.data.assinatura_path }));
            sigPadRef.current.clear();
        } catch (error) {
            setSignatureMessage({ type: 'danger', text: 'Erro ao salvar assinatura no servidor.' });
        } finally {
            setIsSavingSignature(false);
        }
    };

    // Prioriza o preview da nova foto, senão usa a foto existente
    const avatarSrc = photoPreview || (loggedInUser?.foto_path ? `${process.env.REACT_APP_API_URL}/storage/${loggedInUser.foto_path}` : null);

    return (
        <Container className="mt-4 mb-5">
            <Row>
                {/* Coluna da Esquerda: Informações do Perfil */}
                <Col md={4} className="mb-4">
                    <Card className="text-center shadow-sm h-100">
                        <Card.Body className="d-flex flex-column align-items-center">
                            {/* Input de arquivo escondido */}
                            <input 
                                type="file" 
                                ref={fileInputRef} 
                                onChange={handlePhotoChange} 
                                style={{ display: 'none' }} 
                                accept="image/png, image/jpeg, image/jpg"
                            />
                            {avatarSrc ? (
                                <Image 
                                    src={avatarSrc} 
                                    roundedCircle 
                                    style={{ 
                                        width: '120px', 
                                        height: '120px', 
                                        aspectRatio: '1 / 1', // Garante a proporção quadrada
                                        objectFit: 'cover', 
                                        border: '3px solid #0d6efd' 
                                    }} 
                                    className="mb-3"
                                    alt="Foto do perfil"
                                    onClick={() => fileInputRef.current.click()} // Abre o seletor de arquivos ao clicar
                                />
                            ) : (
                                <div 
                                    className="mb-3 d-flex justify-content-center align-items-center rounded-circle bg-primary text-white"
                                    style={{ 
                                        width: '120px', 
                                        height: '120px', 
                                        fontSize: '2.5rem', // Tamanho da fonte para as iniciais
                                        border: '3px solid #0d6efd'
                                    }} 
                                    onClick={() => fileInputRef.current.click()} // Abre o seletor de arquivos ao clicar
                                >
                                    {/* Função para pegar as iniciais do nome */}
                                    {userData.nome.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                                </div>
                            )}
                            <Button variant="outline-primary" size="sm" onClick={() => fileInputRef.current.click()}>
                                Alterar Foto
                            </Button>
                            <Card.Title as="h4">{userData.nome}</Card.Title>
                            <Card.Text className="text-muted">{userData.profile_name}</Card.Text>
                            <hr />
                            <div className="text-start">
                                <p><i className="bi bi-envelope-fill me-2 text-primary"></i>{userData.email}</p>
                                <p><i className="bi bi-person-badge-fill me-2 text-primary"></i>{userData.cpf}</p>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Coluna da Direita: Formulário de Edição */}
                <Col md={8}>
                    <Card className="shadow-sm">
                        <Card.Header as="h5" className="bg-primary text-white">Editar Perfil</Card.Header>
                        <Card.Body className="p-4">
                            {message && <Alert variant={message.type}>{message.text}</Alert>}
                            <Form onSubmit={handleSubmit}>
                                {/* --- Seção de Dados Pessoais Editáveis --- */}
                                <h5 className="mb-3">Informações Pessoais</h5>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="formTelefone">
                                            <Form.Label>Celular (com DDD)</Form.Label>
                                            <InputMask
                                                mask="(99) 99999-9999"
                                                value={userData.telefoneCompleto}
                                                onChange={handleChange}
                                                name="telefoneCompleto"
                                            >
                                                {(inputProps) => <Form.Control {...inputProps} type="tel" />}
                                            </InputMask>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="formDataNascimento">
                                            <Form.Label>Data de Nascimento</Form.Label>
                                            <Form.Control type="date" name="data_nascimento" value={userData.data_nascimento} onChange={handleChange} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="formEstadoCivil">
                                            <Form.Label>Estado Civil</Form.Label>
                                            <Form.Select name="estado_civil" value={userData.estado_civil} onChange={handleChange}>
                                                <option value="Nao informado">Não informado</option>
                                                <option value="Solteiro">Solteiro(a)</option>
                                                <option value="Casado">Casado(a)</option>
                                                <option value="Divorciado">Divorciado(a)</option>
                                                <option value="Viuvo">Viúvo(a)</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="formMae">
                                            <Form.Label>Nome da Mãe</Form.Label>
                                            <Form.Control type="text" name="mae" value={userData.mae} onChange={handleChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="formPai">
                                            <Form.Label>Nome do Pai</Form.Label>
                                            <Form.Control type="text" name="pai" value={userData.pai} onChange={handleChange} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <hr className="my-4" />

                                {/* --- Seção de Alteração de Senha --- */}
                                <h5 className="mb-3">Alterar Senha</h5>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="formNewPassword">
                                            <Form.Label>Nova Senha</Form.Label>
                                            <Form.Control type="password" name="new_password" value={passwordData.new_password} onChange={handlePasswordChange} placeholder="Deixe em branco para não alterar" />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="formConfirmPassword">
                                            <Form.Label>Confirmar Nova Senha</Form.Label>
                                            <Form.Control type="password" name="new_password_confirmation" value={passwordData.new_password_confirmation} onChange={handlePasswordChange} />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr className="my-4" />

                                {/* --- Seção de Assinatura Profissional --- */}
                                <h5 className="mb-3">
                                    <FontAwesomeIcon icon={faFileSignature} className="me-2 text-primary" />
                                    Assinatura Profissional
                                </h5>
                                <p className="text-muted small">Desenhe sua assinatura no quadro abaixo. Ela será utilizada automaticamente para assinar contratos e documentos da clínica.</p>
                                
                                {signatureMessage && <Alert variant={signatureMessage.type} dismissible onClose={() => setSignatureMessage(null)}>{signatureMessage.text}</Alert>}

                                <Row className="mb-3">
                                    <Col md={12}>
                                        {userData.assinatura_path && (
                                            <div className="mb-3 p-2 bg-light border rounded shadow-sm d-inline-block">
                                                <small className="text-muted d-block mb-2 text-center">Assinatura atual registrada:</small>
                                                <Image 
                                                    src={`${process.env.REACT_APP_API_URL}/storage/${userData.assinatura_path}`} 
                                                    style={{ maxHeight: '80px', backgroundColor: '#fff', border: '1px solid #dee2e6' }} 
                                                    alt="Assinatura registrada"
                                                />
                                            </div>
                                        )}
                                        
                                        <div className="border border-2 rounded-3 bg-white mb-3" style={{ borderStyle: 'dashed' }}>
                                            <SignatureCanvas 
                                                ref={sigPadRef}
                                                penColor="black"
                                                backgroundColor="rgba(0,0,0,0)"
                                                canvasProps={{
                                                    className: 'signature-canvas',
                                                    style: { width: '100%', height: '180px', cursor: 'crosshair' }
                                                }}
                                            />
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button variant="outline-secondary" size="sm" onClick={handleClearSignature}>
                                                <FontAwesomeIcon icon={faEraser} className="me-1" /> Limpar
                                            </Button>
                                            <Button variant="success" size="sm" onClick={handleSaveSignature} disabled={isSavingSignature}>
                                                {isSavingSignature ? <Spinner as="span" animation="border" size="sm" /> : <><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Salvar Assinatura</>}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="text-end mt-3">
                                    <Button variant="primary" type="submit" disabled={isLoading}>
                                        {isLoading ? <Spinner as="span" animation="border" size="sm" className="me-2" /> : null}
                                        {isLoading ? 'Salvando...' : 'Salvar Alterações'}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default PerfilUsuarioPage;
